// The evidence for the 90 day review of the conditional second reference.
// Migration 2803.
//
// The second reference became conditional so its cost could be weighed against
// what it catches. Every figure here is computed by
// interview_reference_report_v1; this only lays them out. A figure that does
// not exist yet says so in words rather than showing 0.
import { useEffect, useState } from 'react'
import { BarChart3 } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../lib/shadcn/dialog'
import { useGetReferenceReport } from '../../hooks/backend/interviews'
import { pathLabel } from './ReferenceRule'

type Report = {
  as_of: string
  policy_in_force: string | null
  by_month: { month: string; path: string; decisions: number; offers: number }[]
  second_reference: {
    decisions: number
    b_required: number
    b_not_required: number
    triggers: { trigger: string; times: number }[]
  }
  b_after_a: { a_passed: number; a_passed_b_required: number; b_failed_after_a_passed: number }
  days_to_decision: { path: string; decisions: number; average_days: number | null }[]
  unreachable: {
    applicants_with_referees: number
    unreachable_per_applicant: number | null
    applicants_with_any_unreachable: number
    hold_after: number | null
    applicants_held: number
    holds_released: number
  }
  retention: {
    v2_hires_at_90_days: number
    by_path: { path: string; hires: number; reached_90_days: number; employed_at_90_days: number }[]
  }
}

const TRIGGER_LABEL: Record<string, string> = {
  borderline: 'Reference A scored inside the borderline band',
  truth: 'Interview 2 truth average below the trigger',
  no_uber_record: 'No qualifying Uber record',
  interviewer: 'A scoring interviewer asked',
  coordination: 'Referee number shared with another applicant',
}

function monthLabel(m: string): string {
  const [y, mo] = m.split('-').map(Number)
  if (!y || !mo) return m
  return new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString('en-ZA', { month: 'short', year: 'numeric', timeZone: 'UTC' })
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-1.5">
      <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h5>
      {children}
    </section>
  )
}

function Table({ head, rows, empty }: { head: string[]; rows: (string | number)[][]; empty: string }) {
  if (rows.length === 0) return <p className="text-xs text-muted-foreground">{empty}</p>
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full text-xs">
        <thead className="bg-muted text-muted-foreground">
          <tr>
            {head.map((h, i) => (
              <th key={h} className={i === 0 ? 'px-2 py-1.5 text-left' : 'px-2 py-1.5 text-right'}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} className="border-t border-border">
              {r.map((c, ci) => (
                <td key={ci} className={ci === 0 ? 'px-2 py-1.5' : 'px-2 py-1.5 text-right tabular-nums'}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function ReferenceRuleReportBody({ report }: { report: Report }) {
  const sr = report.second_reference
  const ba = report.b_after_a
  const un = report.unreachable
  return (
    <div className="space-y-4" data-testid="reference-report">
      <p className="text-xs text-muted-foreground">
        As of {report.as_of}. Rule in force: {report.policy_in_force ?? 'none'}.
      </p>

      <Section title="Interview 2 decisions by month and path">
        <Table
          head={['Month', 'Path', 'Decisions', 'Offers']}
          rows={report.by_month.map((r) => [monthLabel(r.month), pathLabel(r.path), r.decisions, r.offers])}
          empty="No Interview 2 decision is recorded yet."
        />
      </Section>

      <Section title="How often the second reference was required">
        {sr.decisions === 0 ? (
          <p className="text-xs text-muted-foreground">No decision has been recorded under the conditional rule yet.</p>
        ) : (
          <>
            <p className="text-xs text-foreground">
              Required in {sr.b_required} of {sr.decisions} decisions under the conditional rule; not required in {sr.b_not_required}.
            </p>
            <Table
              head={['Trigger', 'Times it fired']}
              rows={sr.triggers.map((t) => [TRIGGER_LABEL[t.trigger] ?? t.trigger, t.times])}
              empty="No trigger has fired yet."
            />
          </>
        )}
      </Section>

      <Section title="Did the second call catch anything?">
        {ba.a_passed === 0 ? (
          <p className="text-xs text-muted-foreground">No decision under the conditional rule has a passed Reference A yet.</p>
        ) : (
          <p className="text-xs text-foreground">
            Reference A passed in {ba.a_passed} decisions. A second was required in {ba.a_passed_b_required} of them, and a second
            reference failed after A had passed in <span className="font-semibold">{ba.b_failed_after_a_passed}</span>.
          </p>
        )}
      </Section>

      <Section title="Average days from Interview 2 to decision">
        <Table
          head={['Path', 'Decisions', 'Average days']}
          rows={report.days_to_decision.map((d) => [pathLabel(d.path), d.decisions, d.average_days ?? 'no figure'])}
          empty="No Interview 2 decision is recorded yet."
        />
      </Section>

      <Section title="Unreachable referees">
        <p className="text-xs text-foreground">
          {un.applicants_with_referees === 0
            ? 'No referee has been added yet.'
            : `${un.unreachable_per_applicant ?? 'no figure'} unreachable per applicant, across ${un.applicants_with_referees} applicants with referees. ${un.applicants_with_any_unreachable} had at least one unreachable. `}
          {un.hold_after != null
            ? `${un.applicants_held} reached the hold at ${un.hold_after}; ${un.holds_released} holds released by a manager.`
            : 'No hold applies under the rule in force.'}
        </p>
      </Section>

      <Section title="Still employed at 90 days, by path">
        {report.retention.v2_hires_at_90_days === 0 && (
          <p className="text-xs text-muted-foreground">No hires at 90 days yet under the conditional rule.</p>
        )}
        <Table
          head={['Path', 'Hires', 'Reached 90 days', 'Employed at 90 days']}
          rows={report.retention.by_path.map((r) => [
            pathLabel(r.path),
            r.hires,
            r.reached_90_days,
            r.reached_90_days === 0 ? 'no hires at 90 days yet' : r.employed_at_90_days,
          ])}
          empty="No hire from a scored Interview 2 has started yet."
        />
      </Section>
    </div>
  )
}

export function ReferenceRuleReportButton() {
  const [open, setOpen] = useState(false)
  const get = useGetReferenceReport<Report>()

  useEffect(() => {
    if (open) get.trigger({}, { skipCache: true }).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="reference-report-button">
        <BarChart3 className="h-4 w-4" /> Reference rule evidence
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Reference rule evidence</DialogTitle>
            <DialogDescription>
              For the 90 day review: whether the second reference earns its cost. Every figure comes from the decisions recorded.
            </DialogDescription>
          </DialogHeader>
          {get.error && !get.data ? (
            <p className="text-sm text-destructive">The report could not be loaded. {get.error}</p>
          ) : !get.data ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner className="h-4 w-4" /> Loading the report…
            </p>
          ) : (
            <ReferenceRuleReportBody report={get.data} />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
